import { useCallback, useState } from 'react'

import { BillType } from '@features/bills/enums/billType'
import { InvoiceType } from '@features/invoicesLegacy/types/invoiceType'
import {
  useCreateSubjectReconciliationSuggestion,
  useCreateSubjectsAndBankLinesReconciliation,
} from '@features/paymentReconciliationModal/hooks'
import { BankMatchUiFlowValue, UiSourceValue } from '@features/reconciliation/enums'
import { ReconciliationSuggestion } from '@modules-deprecated/bankReconciliation/types'

import { getReconcileInformationFromSuggestion } from '../utils/getReconcileInformationFromSuggestion'

interface UseCreateMultipleReconciliationsProps {
  onSettled?: (succeededMatches: string[], failedMatches: string[]) => void
  onError?: (error: unknown) => void
  uiSource?: UiSourceValue
  uiFlow?: BankMatchUiFlowValue
}

interface CreateMultipleReconciliationsParams {
  matchSuggestions: ReconciliationSuggestion[]
  organizationId: string
}

export const useCreateMultipleReconciliations = (props: UseCreateMultipleReconciliationsProps) => {
  const { onSettled, onError, uiSource, uiFlow } = props || {}
  const [isProcessing, setIsProcessing] = useState(false)

  const { createAsync: createSubjectsAndBankLinesReconciliation } = useCreateSubjectsAndBankLinesReconciliation({})
  const { createAsync: createSubjectReconciliationSuggestion } = useCreateSubjectReconciliationSuggestion()

  const createMultipleReconciliations = useCallback(
    async ({ matchSuggestions, organizationId }: CreateMultipleReconciliationsParams) => {
      setIsProcessing(true)
      const succeededMatches: string[] = []
      const failedMatches: string[] = []

      if (!matchSuggestions.length) {
        setIsProcessing(false)
        onSettled?.([], [])
        return
      }

      try {
        for (const suggestion of matchSuggestions) {
          const matchId = suggestion.transactions?.[0]?.match_id
          const typeOfSuggestion = suggestion.reconcilables?.[0]?.type

          if (!matchId || !typeOfSuggestion) {
            continue
          }

          try {
            if (
              typeOfSuggestion === BillType.Bill ||
              typeOfSuggestion === InvoiceType.Invoice ||
              typeOfSuggestion === InvoiceType.ExternalInvoice
            ) {
              const { banklineId, subject } = getReconcileInformationFromSuggestion(suggestion)
              await createSubjectsAndBankLinesReconciliation({
                bankLinesIds: [banklineId],
                organizationId,
                subjects: subject,
                parameters: {
                  isAutoMatch: true,
                },
                uiSource,
                uiFlow,
              })
            } else {
              const reconcilableId = suggestion.reconcilables?.[0]?.id
              const subjectId = reconcilableId.split(`${typeOfSuggestion}:`)[1]
              await createSubjectReconciliationSuggestion({
                subjectId,
                subjectType: typeOfSuggestion,
                isAutoMatch: true,
                matchId,
                uiSource,
                uiFlow,
              })
            }
            succeededMatches.push(matchId)
          } catch {
            failedMatches.push(matchId)
          }
        }

        onSettled?.(succeededMatches, failedMatches)
      } catch (error) {
        onError?.(error)
      } finally {
        setIsProcessing(false)
      }
    },
    [
      onError,
      onSettled,
      createSubjectReconciliationSuggestion,
      createSubjectsAndBankLinesReconciliation,
      uiFlow,
      uiSource,
    ],
  )

  return { createMultipleReconciliations, isProcessing }
}
